import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectiDB } from "../features/counter/counterSlice";
import { clearUpStorage, logoutCleanUp, clearMsalKeys } from "../utils";
import useAuth from "./useAuth";
import { usePortal } from "./usePortal";
import { isPWAInstalled } from "../utils/isPWAInstalled";

/**
 * Race a promise against a timeout. Rejects with an Error if the timeout
 * fires first so callers can .catch() and continue without blocking.
 */
const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`Timed out after ${ms}ms`)), ms)),
  ]);

const INSTITUTION_LOGOUT_KEYS = [
  "tgt",
  "institution_auth",
  "institution_data",
  "institution_session",
  "bootstrapReferrer",
  "savedReferrer",
];

/**
 * Build portal URL with post-logout params.
 * - returnTo: always included so the portal can redirect the user back.
 * - r: the saved referrer URL, included for referral and proxy users so the
 *   portal can pass it back as ?r= when the user clicks "Continue via
 *   institution network", enabling re-auth via their proxy or referral URL.
 *   IP-only users have no saved referrer so this param is omitted for them.
 */
const buildPostLogoutPortalUrl = (savedReferrer, basePortalUrl) => {
  const portalUrl = new URL(basePortalUrl);
  portalUrl.searchParams.set("postLogout", "true");

  const returnToUrl = new URL(window.location.origin);
  if (savedReferrer) {
    returnToUrl.searchParams.set("r", savedReferrer);
  }
  portalUrl.searchParams.set("returnTo", returnToUrl.toString());

  return portalUrl.toString();
};

const buildPortalAppLogoutUrl = (basePortalUrl) => {
  const portalUrl = new URL(basePortalUrl + "/signout");
  portalUrl.searchParams.set("appLogout", "true");
  return portalUrl.toString();
};

export default function useAccountActions() {
  const { instance } = useMsal();
  const iDB = useSelector(selectiDB);
  const { isInstitution, setLoggingOut } = useAuth();

  const { isPortalEnabled, portalUrl } = usePortal();
  const [, setSearchParams] = useSearchParams();

  const isIOSPWA = useCallback(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }
    const userAgent = navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(userAgent);
    const isStandaloneMode =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true;
    return isIOS && isStandaloneMode;
  }, []);

  const cleanUpCaches = useCallback(async () => {
    try {
      await logoutCleanUp(iDB);
      const cache = await caches.open(import.meta.env.REACT_APP_CACHE_NAME);
      const ccmsversion = `${import.meta.env.REACT_APP_CCMS_VERSION}`;
      const files = [`${ccmsversion}.zip`, `${ccmsversion}_images.zip`, `${ccmsversion}_PDFs.zip`];
      await Promise.all(
        files.map(async (file) => {
          const url = `/viewTopic/${file}?__WB_REVISION__=${import.meta.env.REACT_APP_CONTENT_VERSION}`;
          const deleted = await cache.delete(url);
          console.log(`${deleted ? "-- deleted" : "?? not found"}: ${url}`);
        }),
      );
    } catch (error) {
      console.warn("Cache cleanup error:", error);
    }
  }, [iDB]);

  const logoutInstitution = useCallback(
    async ({ skipInstitutionPostLogoutRedirect = false } = {}) => {
      try {
        // Snapshot referrer BEFORE clearing storage — bootstrapReferrer and
        // savedReferrer are in INSTITUTION_LOGOUT_KEYS and will be removed.
        // Referral and proxy users need it passed to the portal as ?r= so the
        // portal can forward it back on reconnect for re-auth via their URL.
        const savedReferrer =
          localStorage.getItem("bootstrapReferrer") || localStorage.getItem("savedReferrer") || "";

        clearUpStorage();
        INSTITUTION_LOGOUT_KEYS.forEach((key) => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });
        sessionStorage.clear();
        await withTimeout(cleanUpCaches(), 3000).catch((e) =>
          console.warn("Cache cleanup did not complete before logout redirect:", e),
        );

        if (!isPortalEnabled) {
          sessionStorage.setItem("explicitLogout", "true");
          sessionStorage.setItem("explicitLogout_institution", "true");
          window.location.replace("/");
          return;
        }

        const isInstalledPWA = isIOSPWA() || isPWAInstalled();

        if (isInstalledPWA) {
          // Installed PWAs can behave poorly on cross-origin institution logout.
          // Keep the user in-app so LoginPage can show the reconnect action.
          sessionStorage.setItem("explicitLogout", "true");
          sessionStorage.setItem("explicitLogout_institution", "true");
          console.log(
            "Institution logout detected in installed PWA; skipping portal redirect and returning to local login.",
          );
          window.location.replace("/");
          return;
        }

        if (skipInstitutionPostLogoutRedirect) {
          window.location.replace(portalUrl);
          return;
        }

        const builtPortalUrl = buildPostLogoutPortalUrl(savedReferrer, portalUrl);
        window.location.replace(builtPortalUrl);
      } catch (error) {
        console.error("Institution logout error:", error);
        sessionStorage.clear();
        sessionStorage.setItem("explicitLogout", "true");
        sessionStorage.setItem("explicitLogout_institution", "true");
        window.location.replace("/");
      }
    },
    [cleanUpCaches, isIOSPWA, isPortalEnabled, portalUrl],
  );

  const logoutB2C = useCallback(
    async ({ isLoadingInIframe = false } = {}) => {
      try {
        clearUpStorage();
        sessionStorage.setItem("explicitLogout", "true");
        await withTimeout(cleanUpCaches(), 3000).catch((e) =>
          console.warn("Cache cleanup did not complete before logout redirect:", e),
        );

        if (isIOSPWA()) {
          // iOS PWA may block the MSAL redirect. Schedule a same-origin
          // fallback so the user isn't left on a blank screen.
          const fallbackTimer = setTimeout(() => {
            window.location.replace("/");
          }, 2000);
          window.addEventListener("pagehide", () => clearTimeout(fallbackTimer), { once: true });
        }

        if (isLoadingInIframe) {
          clearMsalKeys();
          // skip the B2C redirect
          console.log("Completed logout in iframe!");
          setLoggingOut(false);
          setSearchParams((prev) => {
            prev.set("signout-success", "1");
            return prev;
          });
          return;
        }

        if (isPWAInstalled() || !isPortalEnabled || isIOSPWA()) {
          await instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
          });
          return;
        }

        // Portal is running - redirect and let the logout trigger from there
        const portalSignoutDestination = buildPortalAppLogoutUrl(portalUrl);
        window.location.assign(portalSignoutDestination);

        // await instance.logoutRedirect({
        //   postLogoutRedirectUri: buildPortalAppLogoutUrl(portalUrl),
        // });
      } catch (error) {
        console.error("B2C logout error:", error);
        clearUpStorage();
        sessionStorage.setItem("explicitLogout", "true");
        window.location.replace("/");
      }
    },
    [cleanUpCaches, instance, isIOSPWA, isPortalEnabled, portalUrl, setLoggingOut, setSearchParams],
  );

  const logout = useCallback(
    async (options = {}) => {
      console.log("Logout initiated. isInstitution:", isInstitution);
      if (isInstitution) {
        await logoutInstitution(options);
      } else {
        await logoutB2C(options);
      }
    },
    [isInstitution, logoutInstitution, logoutB2C],
  );

  return { logout, logoutInstitution, logoutB2C };
}
